import React from 'react';
import styled from '@emotion/styled';
import {
    colors,
    gutterSizes,
    fontSizes,
    mediaQuery,
    breakpoints,
} from '@gumtree/ui-library/src/base/theme';
import FacebookLogoSvg from '@gumtree/shell/src/footer/social-icons/facebook-logo.svg';
import FacebookLogoPng from '@gumtree/ui-library/src/assets/Facebook_Logo_Primary.png';

const shouldForwardProp = (p: string) => !['reviewIsOpen', 'isHidden', 'isLoginPage'].includes(p);

type ForwardProps = {
    isLoginPage?: boolean;
};

export const StyledSocialFormWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${gutterSizes.large18};

    button {
        width: 310px;
    }
`;

export const StyledErrorBox = styled.div`
    background-color: ${colors.branch10};
    padding: ${gutterSizes.medium};
`;

export const StyledErrorTitle = styled.div`
    display: flex;
    align-items: center;
    gap: ${gutterSizes.small};
    margin-bottom: ${gutterSizes.small};
    color: ${colors.red};
    font-size: ${fontSizes.large};
`;

export const StyledErrorDescription = styled.span`
    font-size: ${fontSizes.base};
`;

export const StyledGoogleButtonWrapper = styled('div', {
    shouldForwardProp,
})<ForwardProps>`
    min-width: auto !important;

    ${({ isLoginPage }) =>
        isLoginPage
            ? `

        ${mediaQuery.from(breakpoints.xxsmall)} {
            width: 260px !important;
        }

        ${mediaQuery.from(breakpoints.medium)} {
            width: 400px !important;
        }
    `: `
    width: 400 !important;
    ${mediaQuery.between(breakpoints.xsmall, breakpoints.medium)} {
        width: 310px !important;
    }
`};
`;

export const StyledFacebookButton = styled('button', {
    shouldForwardProp,
})<ForwardProps>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    max-width: 400px;
    padding: ${gutterSizes.base} ${gutterSizes.medium};
    background: ${colors.facebook};
    border: 1px solid ${colors.facebook};
    border-radius: 2px;
    font-size: ${fontSizes.medium};
    color: ${colors.white};
    cursor: pointer;

    ${({ isLoginPage }) =>
        isLoginPage
            ? `

        ${mediaQuery.from(breakpoints.xxsmall)} {
            width: 260px !important;
        }

        ${mediaQuery.from(breakpoints.medium)} {
            width: 400px !important;
        }
    `
            : `
            width: 400 !important;
        ${mediaQuery.between(breakpoints.xsmall, breakpoints.medium)} {
            width: 310px !important;
        }
    `};

    &:hover {
        background-color: ${colors.bark};
    }
`;

export const StyledFacebookLogoPng = styled.div`
    background-image: url('${FacebookLogoPng}');
    background-size: cover;
    height: 24px;
    width: 24px;
    margin-right: ${gutterSizes.base};
`;

export const StyledFacebookLogoSvg = styled(FacebookLogoSvg)`
    position: absolute;
    top: 50%;
    left: ${gutterSizes.medium};
    transform: translateY(-50%);
`;

export const StyledAppleButton = styled('button', {
    shouldForwardProp,
})<ForwardProps>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${gutterSizes.base};
    max-width: 400px;
    padding: ${gutterSizes.base} ${gutterSizes.medium};
    background: #fff;
    border: 1px solid #000000;
    border-radius: 4px;
    color: ${colors.black};
    cursor: pointer;

    ${({ isLoginPage }) =>
        isLoginPage
            ? `
        ${mediaQuery.from(breakpoints.xxsmall)} {
            width: 260px !important;
        }

        ${mediaQuery.from(breakpoints.medium)} {
            width: 400px !important;
        }
    `
            : `
            width: 400px !important;
        ${mediaQuery.between(breakpoints.xsmall, breakpoints.medium)} {
            width: 310px !important;
        }
    `};

    &:hover:not(:disabled) {
        background-color: #f5f5f5;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

export const StyledAppleLogo: React.FC = () =>
    React.createElement(
        'svg',
        {
            width: '20',
            height: '24',
            viewBox: '0 0 17 20',
            fill: '#0B0B0A',
            xmlns: 'http://www.w3.org/2000/svg',
        },
        React.createElement('path', {
            d: 'M8.4 4.8c.9 0 2-.6 2.7-1.4.6-.7 1-1.7 1-2.7 0-.1 0-.3-.1-.4-1 0-2.1.6-2.8 1.4-.6.6-1.1 1.6-1.1 2.6 0 .2 0 .3.1.4.1 0 .1 0 .2.1zM5.1 20c1.3 0 1.8-.8 3.4-.8s2 .8 3.5.8c1.4 0 2.4-1.3 3.3-2.6.4-.5.7-1.1 1-1.6.1-.1.1-.3 0-.4-1.8-.9-3.1-2.9-3.1-5.2 0-2.2 1.3-3.7 2.6-4.5.1-.1.2-.3.1-.4-.9-1.3-2.4-2.2-4.1-2.2-1.6 0-2.7.8-3.6.8-.9 0-2.1-.8-3.5-.8C2.4 3.1 0 5.6 0 9.8c0 2.7 1 5.6 2.3 7.4.9 1.3 2 2.8 3.3 2.8h-.5z',
        })
    );
